import { FileFromResponse } from '@api/models'
import { ISubmittedApplication, SubmittedApplicationApiClient } from 'api'
import { IApplicationStatus } from 'pages/application/model/IApplicationStatus'
import { useSignatureTypes } from 'pages/application/dataProviders/useSignatureTypes'
import { routes } from 'routing'
import { BusyIndicator, DPFormField, DPTextCapitalizeBehavior, FormDebugger, FormikForm, FormSubmit, PageHeader, PageSectionHeader } from '@framework'
import { useApiClient, useSession, useTranslation } from '@hooks'
import { DPInputDate } from 'infrastructure/framework/DPForms/DPInputDate'
import { DPInputDropdown } from 'infrastructure/framework/DPForms/DPInputDropdown'
import { DPInputText } from 'infrastructure/framework/DPForms/DPInputText'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { AgreementCode } from 'shared'
import { addYears, currentDateString } from 'shared/dateTools'
import { isMarineLoanAgreementCode } from 'shared/ApplicationHelper'
import { DPTextTrimBehavior } from 'infrastructure/framework/DPForms/DPTextTrimBehavior'
import styles from './GenerateContractPage.module.css'

interface IForm {
    referenceId: string,
    vehicleRegistrationNumber: string,
    vehicleIdentificationNumber: string,
    deliveryDate: string | undefined,
    signatureType: number
}

export function GenerateContractPage() {
    const navigate = useNavigate()
    const { referenceId } = useParams()
    const apiClient = useApiClient(api => new SubmittedApplicationApiClient(api))
    const [application, setApplication] = useState<ISubmittedApplication>()
    const [applicationStatus, setApplicationStatus] = useState<IApplicationStatus>()
    const [loadingApplication, setLoadingApplication] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState(false)
    const [generatingContract,  setGeneratingContract] = useState(false)
    const [initialValues, setInitialValues] = useState<IForm | undefined>(undefined)

    const $t = useTranslation('GenerateContract')
    const session = useSession()
    const dealerId = session.currentDealer?.id
    const [signatureTypes, loadingSignatureTypes] = useSignatureTypes(dealerId, application?.agreementCode);
    const isMarineLoan = isMarineLoanAgreementCode(application?.agreementCode);
    const effectiveSignatureTypes = application?.hasGuarantor
        ? signatureTypes.filter(x => x.id === 0) //Paper signing only when there is guarantor, because currently we do not support esign for that case. 
        : signatureTypes;
    
    const deliveryDateMin = currentDateString();
    const deliveryDateMax = addYears(deliveryDateMin, 1);

    /*
        Please notice that there is a second argument pass to the .shape() method.
        The seconde argument is not well document (there is an issue to improve it).
        Best explanation what the second argument does can be found here:
        https://github.com/jquense/yup/issues/720#issuecomment-564591045
    */
    const validationSchema = Yup.object().shape({
        vehicleIdentificationNumber: Yup.string().vin({ agreementCode: application?.agreementCode }).label($t('Chassis No.')),
        vehicleRegistrationNumber: Yup.string().registrationNumber().label($t('Registration No.')),
        deliveryDate: Yup
            .string()
            .maxDate(deliveryDateMax)
            .minDate(deliveryDateMin)
            .required()
            .label($t('Delivery date'))
        });

    async function submitValues(values: IForm) {
        const queueContractGeneration = applicationStatus?.shouldQueueContractGeneration ?? false
        if(queueContractGeneration){
            setGeneratingContract(true)
            apiClient.queueContractGeneration(session.currentDealer?.id!, values)
                .then(result => {
                    if (result.success) {
                        navigate(routes.applicationSubmittedApplication(referenceId!))
                    }
                })
                .finally(() => setGeneratingContract(false))
        }
        else{
            setGeneratingContract(true)
            apiClient.generateContract(session.currentDealer?.id!, values)
                .then(result => {
                    if(!result.success){
                        return;
                    }

                    return new FileFromResponse(result.response!).download()
                })
                .finally(() => setGeneratingContract(false))
        }
    }

    useEffect(() => {
        if(!dealerId || !referenceId){
            return
        }

        setLoadingApplication(true)
        setLoadingStatus(true)

        apiClient.getApplication(dealerId, referenceId)
            .then(response => {
                if(!response){
                    return
                }

                setApplication(response);
            })
            .finally(() => setLoadingApplication(false))

        apiClient.getStatus(dealerId, referenceId)
            .then(response => {
                if (response) {
                    setApplicationStatus(response)
                }
            })
            .finally(() => setLoadingStatus(false))

    }, [dealerId, referenceId])

    useEffect(() => {
        if(!application){
            return
        }

        setInitialValues({
            referenceId: application.referenceId,
            vehicleIdentificationNumber: application.objectSerialNumber,
            vehicleRegistrationNumber: application.objectRegistrationNumber,
            deliveryDate: currentDateString(),
            signatureType: effectiveSignatureTypes[0]?.id,
        })         
    }, [application, signatureTypes.length])

    return (
        <div className={styles['container']}>
            <PageHeader text={$t('Generate contract')} />
            <BusyIndicator busy={generatingContract || loadingApplication || loadingSignatureTypes || loadingStatus}>
                {initialValues &&
                    <FormikForm
                        initialValues={initialValues}
                        onSubmit={submitValues}
                        validationSchema={validationSchema}
                    >
                        <PageSectionHeader>
                            {$t('Car information')}
                        </PageSectionHeader>

                        {!isMarineLoan && <DPFormField label={$t('Registration No.')} name="vehicleRegistrationNumber">
                            <DPInputText name="vehicleRegistrationNumber" />
                            <DPTextCapitalizeBehavior name="vehicleRegistrationNumber" />
                        </DPFormField>}

                        <DPFormField
                            label={$t('Chassis No.')}
                            name="vehicleIdentificationNumber"
                            required
                        >
                            <DPInputText name="vehicleIdentificationNumber" />
                            <DPTextCapitalizeBehavior name="vehicleIdentificationNumber" />
                            <DPTextTrimBehavior name="vehicleIdentificationNumber" />
                        </DPFormField>

                        <div className={`inline field dp-form-field ${styles['generation-note-container']}`}>
                            <div className='label-group'></div>
                            <div className={`input-group ${styles['generation-note']}`}>{$t('GenerationNote')}</div>
                        </div>

                        <PageSectionHeader>
                            {$t('Others')}
                        </PageSectionHeader>

                        <DPFormField label={$t('Delivery date')} name="deliveryDate" required>
                            <DPInputDate
                                name="deliveryDate"
                                minDate={deliveryDateMin}
                                maxDate={deliveryDateMax}
                            />
                        </DPFormField>

                        <DPFormField label={$t('Signing type')} name="signatureType" required>
                            <DPInputDropdown
                                name="signatureType"
                                disabled={effectiveSignatureTypes.length === 1}
                                options={effectiveSignatureTypes.map(x => {
                                    return {
                                        key: x.id.toString(),
                                        value: x.id,
                                        text: x.name,
                                    }
                                })}
                            />
                        </DPFormField>

                        <FormSubmit submitName={$t('Generate')} />

                        <FormDebugger />
                    </FormikForm>
                }
            </BusyIndicator>
        </div>
    )
}